import React, { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, X, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { api, getMl } from "@/lib/api";
import MultilingualField from "@/components/MultilingualField";
import FileUploader from "@/components/FileUploader";

const empty = () => ({
  title: { en: "", tr: "", id: "" },
  excerpt: { en: "", tr: "", id: "" },
  body: { en: "", tr: "", id: "" },
  cover_image: "", slug: "", external_link: "", is_published: true,
});

const AdminNews = () => {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/news?published_only=false").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const onSave = async (data) => {
    try {
      if (editing?.id) await api.put(`/news/${editing.id}`, data);
      else await api.post("/news", data);
      toast.success("Saved"); setEditing(null); load();
    } catch { toast.error("Failed"); }
  };

  const onDelete = async (id) => {
    if (!confirm("Delete?")) return;
    await api.delete(`/news/${id}`);
    load();
  };

  return (
    <div data-testid="admin-news">
      <div className="flex justify-between items-center mb-8">
        <h1 className="font-display text-3xl text-[#0A1128]">News & Insights</h1>
        <button onClick={() => setEditing(empty())} data-testid="add-news-btn" className="btn-gold"><Plus size={14} /> Add Article</button>
      </div>
      <div className="bg-white border border-slate-200 rounded-sm">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-[10px] uppercase tracking-[0.2em] text-slate-500">
            <tr>
              <th className="px-4 py-3 text-left">Title</th>
              <th className="px-4 py-3 text-left">Slug</th>
              <th className="px-4 py-3 text-left">Status</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {items.map((n) => (
              <tr key={n.id} className="border-t border-slate-100">
                <td className="px-4 py-3 font-medium">
                  <span className="inline-flex items-center gap-2">
                    {getMl(n.title, "en")}
                    {n.external_link ? (
                      <a href={n.external_link} target="_blank" rel="noopener noreferrer" title={n.external_link} className="text-[#D4AF37] hover:text-[#0A1128]" data-testid={`news-link-icon-${n.id}`}>
                        <ExternalLink size={12} />
                      </a>
                    ) : null}
                  </span>
                </td>
                <td className="px-4 py-3 font-mono text-xs text-slate-500">{n.slug}</td>
                <td className="px-4 py-3"><span className={`text-[10px] px-2 py-1 rounded-sm uppercase ${n.is_published ? "bg-green-100 text-green-700" : "bg-slate-100"}`}>{n.is_published ? "Live" : "Draft"}</span></td>
                <td className="px-4 py-3 text-right">
                  <button onClick={() => setEditing(n)} data-testid={`edit-news-${n.id}`} className="text-slate-400 hover:text-[#0A1128] p-1.5"><Pencil size={14} /></button>
                  <button onClick={() => onDelete(n.id)} data-testid={`del-news-${n.id}`} className="text-slate-400 hover:text-red-500 p-1.5"><Trash2 size={14} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center p-6 overflow-y-auto" data-testid="news-form">
          <div className="bg-white max-w-3xl w-full rounded-sm my-10">
            <div className="flex justify-between items-center p-6 border-b border-slate-200">
              <h2 className="font-display text-2xl">{editing.id ? "Edit" : "Add"} Article</h2>
              <button onClick={() => setEditing(null)}><X size={18} /></button>
            </div>
            <div className="p-6 space-y-5">
              <MultilingualField label="Title" value={editing.title} onChange={(v) => setEditing({ ...editing, title: v })} testId="news-title" required />
              <div>
                <label className="text-[10px] uppercase tracking-[0.25em] text-slate-500 block mb-2">Slug *</label>
                <input value={editing.slug} onChange={(e) => setEditing({ ...editing, slug: e.target.value })} required data-testid="news-slug" className="w-full px-4 py-3 border border-slate-300 rounded-sm text-sm font-mono" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-[0.25em] text-slate-500 block mb-2">External Link (optional)</label>
                <input
                  value={editing.external_link || ""}
                  onChange={(e) => setEditing({ ...editing, external_link: e.target.value })}
                  placeholder="https://example.com/full-article"
                  data-testid="news-external-link"
                  className="w-full px-4 py-3 border border-slate-300 rounded-sm text-sm font-mono"
                />
                <p className="text-[10px] text-slate-400 mt-1.5">If set, clicking the article card opens this URL in a new tab.</p>
              </div>
              <MultilingualField label="Excerpt" value={editing.excerpt} onChange={(v) => setEditing({ ...editing, excerpt: v })} testId="news-excerpt" multiline />
              <MultilingualField label="Body" value={editing.body} onChange={(v) => setEditing({ ...editing, body: v })} testId="news-body" multiline />
              <FileUploader label="Cover Image" value={editing.cover_image} onChange={(v) => setEditing({ ...editing, cover_image: v })} testId="news-cover" />
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={editing.is_published} onChange={(e) => setEditing({ ...editing, is_published: e.target.checked })} data-testid="news-pub" /> Published</label>
            </div>
            <div className="flex justify-end gap-3 p-6 border-t border-slate-200">
              <button onClick={() => setEditing(null)} className="text-xs uppercase tracking-[0.2em] text-slate-500 px-5">Cancel</button>
              <button onClick={() => onSave(editing)} data-testid="news-save" className="btn-gold">Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminNews;
